import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, X, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslations } from '@/i18n';
import { subscribeAuthEvent } from '@/lib/auth/auth-events';

type ToastVariant = 'success' | 'error';

export interface ToastOptions {
  message: string;
  variant?: ToastVariant;
  duration?: number;
}

interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
  duration: number;
}

interface ToastContextValue {
  showToast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const DEFAULT_DURATION = 5000;

export function ToastProvider({ children }: PropsWithChildren) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timersRef = useRef<Map<number, number>>(new Map());
  const t = useTranslations('toasts');

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    const timers = timersRef.current;
    const timerId = timers.get(id);
    if (timerId) {
      clearTimeout(timerId);
      timers.delete(id);
    }
  }, []);

  const showToast = useCallback(
    (options: ToastOptions) => {
      const duration = options.duration ?? DEFAULT_DURATION;
      const id = Date.now() + Math.floor(Math.random() * 1000);
      const next: ToastItem = {
        id,
        message: options.message,
        variant: options.variant ?? 'success',
        duration,
      };

      setToasts((prev) => [...prev, next]);
      timersRef.current.set(
        id,
        window.setTimeout(() => {
          dismissToast(id);
        }, duration)
      );
    },
    [dismissToast]
  );

  useEffect(() => {
    const timers = timersRef.current;

    return () => {
      timers.forEach((timerId) => {
        clearTimeout(timerId);
      });
      timers.clear();
    };
  }, []);

  useEffect(() => {
    return subscribeAuthEvent('permission-denied', () => {
      showToast({
        message: t('permissionDenied'),
        variant: 'error',
      });
    });
  }, [showToast, t]);

  const contextValue = useMemo<ToastContextValue>(
    () => ({
      showToast,
    }),
    [showToast]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {typeof document !== 'undefined'
        ? createPortal(<ToastViewport toasts={toasts} onDismiss={dismissToast} />, document.body)
        : null}
    </ToastContext.Provider>
  );
}

interface ToastViewportProps {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}

function ToastViewport({ toasts, onDismiss }: ToastViewportProps) {
  if (toasts.length === 0) {
    return null;
  }

  return (
    <div
      className="pointer-events-none fixed left-0 right-0 bottom-6 z-[70] flex flex-col items-center gap-3 px-4 sm:left-auto sm:right-10 sm:bottom-12 sm:items-end sm:px-0"
      aria-live="polite"
      aria-atomic="true"
    >
      {toasts.map((toast) => (
        <ToastMessage key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

interface ToastMessageProps {
  toast: ToastItem;
  onDismiss: (id: number) => void;
}

function ToastMessage({ toast, onDismiss }: ToastMessageProps) {
  const Icon = toast.variant === 'error' ? XCircle : CheckCircle2;
  const role = toast.variant === 'error' ? 'alert' : 'status';
  const t = useTranslations('toasts');

  return (
    <div
      role={role}
      className={cn(
        'pointer-events-auto inline-flex w-full max-w-[32rem] items-center justify-between gap-3 rounded-[12px] px-5 py-3 text-white shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.24)] sm:w-auto sm:min-w-[20rem]',
        toast.variant === 'error' ? 'bg-[#e5484d]' : 'bg-[#30a46c]'
      )}
    >
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-white" aria-hidden="true" />
        <span className="text-[15px] leading-6 font-medium">{toast.message}</span>
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition hover:bg-white/10"
        aria-label={t('close')}
      >
        <X className="h-4 w-4 text-white" aria-hidden="true" />
      </button>
    </div>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
